import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCompactMode } from '../../hooks/useCompactMode';

interface CompactModeToggleProps {
    className?: string;
    /**
     * `header` — the standalone glass button in the page headers.
     * `inline` — the same switch parked inside an amount field, so the unit is
     * visible (and changeable) exactly where money is typed. People never found
     * the header one on their own.
     */
    variant?: 'header' | 'inline';
}

/**
 * Currency unit switch: đ (full amounts) ↔ K (thousands).
 * Showing both units side by side makes the active one — and what the "K" means —
 * readable at a glance, which a lone "K" button did not.
 *
 * The header variant names the active unit in full underneath. What "K" multiplies
 * by used to live only in the button's `title`, which a phone never shows, and in a
 * caption beside the event title — the opposite end of the header from the control
 * it explained. Saying it directly under the switch is what connects the two.
 */
const CompactModeToggle: React.FC<CompactModeToggleProps> = ({ className = '', variant = 'header' }) => {
    const { t } = useTranslation();
    const { compactMode, toggleCompactMode } = useCompactMode();

    const isInline = variant === 'inline';

    const segmentClass = (active: boolean) =>
        `${isInline ? 'px-2 py-1' : 'px-1.5 py-0.5'} rounded-lg transition-colors duration-200 ${
            active
                ? 'bg-indigo-500 text-white shadow-sm shadow-indigo-500/30'
                : 'text-gray-400 dark:text-gray-500'
        }`;

    const wrapperClass = isInline
        ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-0.5 font-bold text-xs flex items-center gap-0.5 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors'
        : 'glass-header-back !p-1 !rounded-xl !font-bold !text-xs flex items-center gap-0.5';

    const button = (
        <button
            type="button"
            onClick={toggleCompactMode}
            aria-pressed={compactMode}
            aria-label={t('components.compactToggle.ariaLabel')}
            title={compactMode
                ? t('components.compactToggle.onHint')
                : t('components.compactToggle.offHint')}
            className={`${wrapperClass} ${isInline ? className : ''}`}
        >
            <span className={segmentClass(!compactMode)}>đ</span>
            <span className={segmentClass(compactMode)}>K</span>
        </button>
    );

    if (isInline) return button;

    // 28px button + 2px gap + 10px caption comes to the 40px of the icon buttons
    // beside it, so the column lines up with the rest of the header row instead of
    // needing to be floated out of it.
    return (
        <div className={`flex flex-col items-center gap-0.5 flex-shrink-0 ${className}`}>
            {button}
            <span
                aria-hidden="true"
                className="text-[9px] leading-none whitespace-nowrap text-gray-400 dark:text-gray-500"
            >
                {compactMode
                    ? t('components.compactToggle.unitThousand')
                    : t('components.compactToggle.unitDong')}
            </span>
        </div>
    );
};

export default CompactModeToggle;
